import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { type ProcedureInitiative, type Stored, submittedAtModelSortFn } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { procedureInitiativeFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import {
	ProcedureInitiativeSelectGroup,
	type ProcedureInitiativeSelectGroupProps,
} from "../MenuSelectGroups/ProcedureInitiativeSelectGroup"

type ProcedureInitiativeDropdownSelectProps = Omit<MenuRootProps, "children"> &
	Omit<MenuClickTriggerProps, "children"> &
	MenuVariantProps &
	ProcedureInitiativeSelectGroupProps & {
		children: ReactElement
	}

export const ProcedureInitiativeDropdownSelect = memo<ProcedureInitiativeDropdownSelectProps>((props) => {
	const {
		disabled = false,
		children,
		radius,
		size,
		variant,
		accentColor,
		procedureInitiatives,
		required,
		defaultValue,
		value,
		onValueChange,
		...rest
	} = props

	const sortedProcedureInitiatives = useSort(procedureInitiatives, submittedAtModelSortFn)

	const [filteredProcedureInitiatives, filterValue, setFilterValue] = useTextFilter<Stored<ProcedureInitiative>>(
		sortedProcedureInitiatives,
		procedureInitiativeFilterCallback,
	)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{filteredProcedureInitiatives.length > 0 && <Menu.Separator />}
				<Menu.Scroll>
					{required ? (
						<ProcedureInitiativeSelectGroup
							required={required}
							defaultValue={defaultValue}
							value={value}
							onValueChange={onValueChange}
							procedureInitiatives={filteredProcedureInitiatives}
						/>
					) : (
						<ProcedureInitiativeSelectGroup
							required={required}
							defaultValue={defaultValue}
							value={value}
							onValueChange={onValueChange}
							procedureInitiatives={filteredProcedureInitiatives}
						/>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})

ProcedureInitiativeDropdownSelect.displayName = "ProcedureInitiativeDropdownSelect"
